import { Breadcrumb } from "@twinkltech/curriculum-architect-component-library";
import {
  ApiResponse,
  Curriculum,
  Subject,
  UpdateCurriculumPayloadSchema,
  UpdateSubjectStatusPayload,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Text } from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import { SubjectHelper } from "@/utils/SubjectHelper/SubjectHelper";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { paths } from "@/paths";

import { SubjectStatusDropdown } from "../SubjectStatusDropdown/SubjectStatusDropdown";
import { TabLink } from "../TabLink/TabLink";

import { SUBJECT_PLAN_HEADER_NS } from "./SubjectPlanHeader.i18n";

export const SubjectPlanHeader = ({
  curriculum,
  isLoading,
  isUpdating,
  onUpdateSubjectStatus,
  subject,
}: {
  curriculum: Curriculum;
  isLoading?: boolean;
  isUpdating?: boolean;
  subject: Subject;
  onUpdateSubjectStatus: (params: {
    curriculumId: string;
    subjectId: string;
    payload: UpdateSubjectStatusPayload;
  }) => ApiResponse<Partial<Subject | void>>;
}) => {
  const { t } = useTranslation(SUBJECT_PLAN_HEADER_NS);
  const { click } = useTwinklAnalytics();

  const subjectImage =
    SubjectHelper.getCardDecorations(subject.slug).imageUrl || "";

  return (
    <>
      <Breadcrumb className="mb-6">
        <Breadcrumb.Item>
          <Link to={paths.CURRICULUM_LIST_PATH}>
            <span className="truncate max-w-[200px] block">
              {t("terms.my-curricula", {
                ns: COMMON_NS,
              })}
            </span>
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link
            to={generatePath(paths.CURRICULUM_SUBJECT_LIST_PATH, {
              curriculum_id: curriculum.id,
            })}
          >
            <span className="truncate max-w-[200px] block">
              {curriculum.displayLabel}
            </span>
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item current className="text-gray-500">
          <span className="truncate max-w-[200px] block">
            {SubjectHelper.getLabel({
              slug: subject?.slug,
              displayLabel: subject?.label,
            })}
          </span>
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Subject Details */}
      <div
        aria-label={t("subject-details.title")}
        className="bg-blue-50 p-5 border-b-8 border-b-blue-500 flex gap-6 mb-10 rounded-t-xl"
        role="region"
      >
        {subjectImage !== "" ? (
          <div
            className="w-[80px] h-[80px] bg-contain bg-center bg-no-repeat"
            role="presentation"
            style={{
              backgroundImage: `url(${subjectImage})`,
            }}
          />
        ) : null}
        <div className="max-w-[800px] flex flex-col gap-2">
          <Text as="h1" variant="2xl-medium">
            <span className="line-clamp-2">{subject?.label}</span>
          </Text>
          <Text
            as={Link}
            className="text-[#1C7AC7] hover:text-blue-700 underline max-w-[400px]"
            to={generatePath(paths.CURRICULUM_SUBJECT_LIST_PATH, {
              curriculum_id: curriculum?.id,
            })}
            variant="base-normal"
          >
            <span className="line-clamp-1">{curriculum?.displayLabel}</span>
          </Text>
          <Text as="p">
            {SubjectHelper.getDescription({
              slug: subject?.slug,
            })}
          </Text>
        </div>
      </div>

      {/* Header action bar */}
      <div className="flex justify-between items-center mb-6">
        <nav className="flex gap-3">
          <TabLink
            to={{
              pathname: generatePath(paths.SUBJECT_PLAN_EDIT_PATH, {
                curriculum_id: curriculum?.id,
                subject_id: subject?.id,
              }),
              search: window.location.search, // Preserve query parameters
            }}
            onClick={() => {
              click("click_cta", {
                clickCopy: "Plan units",
              });
            }}
          >
            {t("tabs.plan-units")}
          </TabLink>
          <TabLink
            to={generatePath(paths.SUBJECT_PLAN_OVERVIEW_PATH, {
              curriculum_id: curriculum?.id,
              subject_id: subject?.id,
            })}
            onClick={() => {
              click("click_cta", {
                clickCopy: "Teach",
              });
            }}
          >
            {t("tabs.teach")}
          </TabLink>
        </nav>
        <div>
          <SubjectStatusDropdown
            asDropdown
            curriculumId={curriculum?.id}
            defaultValue={subject?.status}
            disabled={isLoading || isUpdating}
            entityId={curriculum?.id}
            resolver={UpdateCurriculumPayloadSchema}
            subjectId={subject?.id}
            onStatusUpdate={onUpdateSubjectStatus}
          />
        </div>
      </div>
    </>
  );
};
